import React, { useEffect, useRef, useContext } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  getTandaSyncHistoryApi,
  exportTandaSyncHistoryApi,
} from "../api/tandaApi";
import moment from "moment";
import { useDebouncedCallback } from "use-debounce";
import qs from "qs";
import { TableStateContext } from "../../../store";

export const useTandaSyncHistory = (storeId) => {
  const [syncHistory, setSyncHistory] = useImmer({
    isLoading: true,
    data: [],
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    sortBy: [],
    filterApplied: {
      search: "",
      date: {},
      status: null,
      all: [],
    },
    disableApplyButton: false,
    isDatePickerOpen: false,
    date: [
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ],
    showDate: [],
    moreModal: false,
    moreText: "",
  });

  useEffect(() => {
    if (storeId) {
      fetchSyncHistory();
    }
  }, []);

  const debouncedSearch = useDebouncedCallback((value) => {
    const { date, status } = syncHistory.filterApplied;
    const { pagination, sortBy } = syncHistory;

    fetchSyncHistory({
      search: value,
      date,
      status,
      pagination: {
        pageIndex: 1,
        pageSize: pagination.pageSize,
      },
      sortBy,
    });
  }, 1000);

  const handleMoreModal = (open, text = "") => {
    setSyncHistory((draft) => {
      draft.moreModal = open;
      draft.moreText = text;
    });
  };

  const fetchSyncHistory = async ({
    pagination = syncHistory.pagination,
    sortBy = syncHistory.sortBy,
    search = syncHistory.filterApplied.search,
    date = syncHistory.filterApplied.date,
    status = syncHistory.filterApplied.status,
  } = {}) => {
    if (!storeId) return;

    try {
      setSyncHistory((draft) => {
        draft.isLoading = true;
      });

      let params = {
        store_id: storeId,
      };

      if (search) {
        params.search = search;
      }

      if (date && date.value) {
        params.sync_date = moment(date.value).format("YYYY-MM-DD");
      }

      if (status) {
        params.status = status.value;
      }

      if (sortBy && sortBy.length > 0) {
        const sortByParams = sortBy.reduce(
          (obj, item) =>
            Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
          {}
        );

        params = {
          ...params,
          sort: sortByParams,
        };
      }

      const query = {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            encode: true,
            arrayFormat: "brackets",
            encodeValuesOnly: true,
          }),
      };

      const response = await getTandaSyncHistoryApi(query, pagination);

      if (response.success) {
        setSyncHistory((draft) => {
          draft.data = response.data.history || [];
          draft.pagination = response.data.pagination || draft.pagination;
          draft.sortBy = sortBy;

          // Only update filters if they are explicitly passed
          if (search !== undefined) draft.filterApplied.search = search;
          if (date !== undefined) draft.filterApplied.date = date;
          if (status !== undefined) draft.filterApplied.status = status;

          // Update all filters for display
          draft.filterApplied.all = [
            draft.filterApplied.date,
            draft.filterApplied.status,
          ].filter((el) => el && Object.keys(el).length);

          draft.isLoading = false;
        });
      } else {
        errorMessage(response.message || "Failed to fetch sync history");
        setSyncHistory((draft) => {
          draft.isLoading = false;
        });
      }
    } catch (error) {
      console.error("Error fetching sync history:", error);
      errorMessage(error.response?.data?.message || "Something went wrong");
      setSyncHistory((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleSearch = (value) => {
    setSyncHistory((draft) => {
      draft.filterApplied.search = value;
    });
    debouncedSearch(value);
  };

  const onChangeDate = (e) => {
    setSyncHistory((draft) => {
      draft.filterApplied.date = e;
      draft.disableApplyButton = false;
    });
  };

  const handleIsOpen = (isOpen) => {
    setSyncHistory((draft) => {
      draft.isDatePickerOpen = isOpen;
    });
  };

  const handleStatusChange = (status) => {
    setSyncHistory((draft) => {
      draft.filterApplied.status = status;
      draft.disableApplyButton = false;
    });
  };

  const handleApplyFilter = () => {
    const { search, date, status } = syncHistory.filterApplied;
    const { pagination, sortBy } = syncHistory;

    fetchSyncHistory({
      search,
      date,
      status,
      pagination: {
        pageIndex: 1,
        pageSize: pagination.pageSize,
      },
      sortBy,
    });
  };

  const handleFilterClear = (type, filter) => {
    if (type === "single") {
      if (filter.type === "Sync Date") {
        setSyncHistory((draft) => {
          draft.filterApplied.date = {};
        });
      }

      if (filter.type === "Status") {
        setSyncHistory((draft) => {
          draft.filterApplied.status = null;
        });
      }

      setSyncHistory((draft) => {
        draft.disableApplyButton = false;
      });
    } else {
      setSyncHistory((draft) => {
        draft.filterApplied = {
          search: draft.filterApplied.search, // Keep search
          date: {},
          status: null,
          all: [],
        };
        draft.isLoading = true;
      });

      fetchSyncHistory({
        search: syncHistory.filterApplied.search,
        pagination: {
          pageIndex: 1,
          pageSize: syncHistory.pagination.pageSize,
        },
        sortBy: syncHistory.sortBy,
        date: {},
        status: null,
      });
    }
  };

  const gotoPage = (page) => {
    const { search, date, status } = syncHistory.filterApplied;
    const { sortBy } = syncHistory;

    fetchSyncHistory({
      search,
      date,
      status,
      pagination: {
        pageIndex: page,
        pageSize: syncHistory.pagination.pageSize,
      },
      sortBy,
    });

    setSyncHistory((draft) => {
      draft.pagination.pageIndex = page;
    });
  };

  const setPageSize = (e) => {
    const { search, date, status } = syncHistory.filterApplied;
    const { sortBy } = syncHistory;

    fetchSyncHistory({
      search,
      date,
      status,
      pagination: {
        pageIndex: 1,
        pageSize: +e.target.value,
      },
      sortBy,
    });

    setSyncHistory((draft) => {
      draft.pagination.pageSize = +e.target.value;
    });
  };

  const handleSort = (sortBy) => {
    const { search, date, status } = syncHistory.filterApplied;
    const { pagination } = syncHistory;

    fetchSyncHistory({
      search,
      date,
      status,
      pagination,
      sortBy,
    });

    setSyncHistory((draft) => {
      draft.sortBy = sortBy;
    });
  };

  // Update the all filters array when individual filters change
  useEffect(() => {
    setSyncHistory((draft) => {
      draft.filterApplied.all = [
        syncHistory.filterApplied.date,
        syncHistory.filterApplied.status,
      ].filter((el) => el && Object.keys(el).length);
    });
  }, [syncHistory.filterApplied.date, syncHistory.filterApplied.status]);

  // Export function if needed

  return {
    syncHistory,
    isLoading: syncHistory.isLoading,
    handleSearch,
    handleSort,
    gotoPage,
    setPageSize,
    handleMoreModal,
    handleIsOpen,
    onChangeDate,
    handleStatusChange,
    handleFilterClear,
    handleApplyFilter,
  };
};
